import { Button } from '@src/components/button/Button.component'
import { InputNc } from '@src/components/input-nc/InputNc.component'
import { SignupButton } from '@src/components/signup-button/SignupButton.component'
import { checkValidation, ValidationType } from '@src/helpers/checkValidation'
import { useAppDispatch } from '@src/hooks/useAppDispatch'
import { useTranslation } from '@src/hooks/useTranslation'
import { mixpanelService } from '@src/srv/mixpanel.service'
import { newsletterService } from '@src/srv/newsletter.service'
import { setDiscountCode, setDiscountPercentage } from '@src/store/discountCode/discountCode.slice'
import classNames from 'classnames'
import { DatoCmsFileField } from 'dato-cms-assets'
import type { ChangeEvent, SetStateAction } from 'react'
import { ButtonHTMLAttributes, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { Checkbox } from '../checkbox/Checkbox.component'
import { NcImage } from '../nc-image/NcImage.component'
import styles from './PopupLeadCapture.module.scss'

export interface PopupLeadCaptureProps {
  name: string
  isOpen: boolean
  _modelApiKey: 'lead_capture_popup'
  formTitle: string
  formText: string
  emailInputLabel: string
  emailInputPlaceholder: string
  buttonText: string
  successTitle: string
  successText: string
  promoCode?: string
  discountPercentage: string
  image: DatoCmsFileField
  discountLine: string
  saveDiscountButton: string
  signupDiscountButton: string
  secondaryButton: string
  consent: string
  onDismiss: () => void
}

enum PopupLeadCaptureSteps {
  Form = 'Form',
  Success = 'Success',
  Signup = 'Signup',
}

export function PopupLeadCapture({
  name,
  isOpen,
  formTitle,
  formText,
  emailInputLabel,
  buttonText,
  successText,
  successTitle,
  promoCode,
  discountPercentage,
  image,
  discountLine,
  saveDiscountButton,
  signupDiscountButton,
  secondaryButton,
  consent,
  onDismiss,
}: PopupLeadCaptureProps): ReactNode {
  const dispatch = useAppDispatch()
  const button = useRef<any>()
  const translate = useTranslation()
  const hasOpened = useRef(false)
  const [email, setEmail] = useState('')
  const [step, setStep] = useState(PopupLeadCaptureSteps.Form)
  const [error, setError] = useState(false)
  const [isConsentChecked, setIsConsentChecked] = useState(false)

  const withImage = step === PopupLeadCaptureSteps.Form
  const className = classNames(styles.PopupLeadCapture, {
    [styles.PopupLeadCapture___withImage]: withImage,
  })

  const onOpen = useCallback(() => {
    // Focusing an element inside the modal avoids the problem of the modal
    // appearing visually but going unnoticed by screen readers, whose focus
    // remains on the main page content behind, often with no clear path forward
    // for the user to rectify the situation.
    button.current.focus()

    hasOpened.current = true
    mixpanelService.track('LeadCapturePopupImpression', {
      name,
    })
  }, [])

  const onError = useCallback(() => {
    setError(true)
    mixpanelService.track('LeadCapturePopupError', {
      name,
    })
  }, [])

  const onSuccess = useCallback(() => {
    if (promoCode) {
      setStep(PopupLeadCaptureSteps.Signup)
    } else {
      setStep(PopupLeadCaptureSteps.Success)
    }
    mixpanelService.track('LeadCapturePopupSuccess', {
      name,
    })
  }, [email])

  const onSubmit = useCallback(() => {
    if (error) {
      return
    }

    if (!checkValidation(email, ValidationType.Email)) {
      return setError(true)
    }

    const consentTo = [formTitle, formText, buttonText].join('\n')

    newsletterService
      .addToNewsletterAccount({ email, source: 'Popup', campaign: name, consentTo })
      .then(() => onSuccess())
      .catch(() => onError())
  }, [email])

  const onClose = useCallback(() => {
    mixpanelService.track('LeadCapturePopupClose', {
      name,
    })
  }, [])

  useEffect(() => {
    if (isOpen) {
      onOpen()
    } else if (hasOpened.current) {
      onClose()
    }
  }, [isOpen])

  const buttonProps: ButtonHTMLAttributes<HTMLButtonElement> = {
    'aria-label': translate('popup-close-button'),
    className: styles.PopupLeadCapture__closeButton,
    onClick: onDismiss,
  }

  if (discountPercentage.length < 1) {
    discountPercentage = ''
  }

  const onDismissWithSecondaryButton = useCallback(() => {
    mixpanelService.track('PopupLeadCaptureDismissDiscountCode', {
      name,
    })

    onDismiss()
  }, [dispatch])

  const saveDiscountCode = useCallback(() => {
    if (promoCode) {
      dispatch(setDiscountCode(promoCode, 'LeadCapturePopup'))
    }
    dispatch(setDiscountPercentage(discountPercentage))
    mixpanelService.track('PopupLeadCaptureSaveDiscountCode', {
      name,
    })
    onDismiss()
  }, [dispatch])

  const handleChange = useCallback((email: SetStateAction<string>) => {
    setError(false)
    setEmail(email)
  }, [])

  const handleCheckboxChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setIsConsentChecked(event.target.checked)
  }, [])

  return (
    <div className={className}>
      <button {...buttonProps} ref={button} uid="popup-lead-capture-close-button">
        <svg height={28} width={28} xmlns="http://www.w3.org/2000/svg" viewBox="-4 -4 42 42">
          <circle
            cx={17.5}
            cy={17.5}
            r={20}
            className={styles.PopupLeadCapture__closeIconBackground}
          />
          <polygon
            className={styles.PopupLeadCapture__closeIconForeground}
            points="27.661 9.366 26.325 8 18 16.63 9.675 8 8.339 9.366 16.661 18 8.339 26.634 9.675 28 18 19.37 26.325 28 27.661 26.634 19.339 18 27.661 9.366"
          />
        </svg>
      </button>
      {withImage && (
        <div className={styles.PopupLeadCapture__image}>
          <NcImage {...image} objectFit="contain" loading="eager" />
        </div>
      )}
      <div className={styles.PopupLeadCapture__content}>
        {step === PopupLeadCaptureSteps.Form && (
          <div className={styles.PopupLeadCapture__formStep}>
            <div
              id="popup-title"
              className={styles.PopupLeadCapture__formTitle}
              dangerouslySetInnerHTML={{ __html: formTitle }}
            />
            <div
              id="popup-description"
              className={styles.PopupLeadCapture__formText}
              dangerouslySetInnerHTML={{ __html: formText }}
            />
            <div className={styles.PopupLeadCapture__emailInput}>
              <InputNc
                setValue={handleChange}
                id="email-input"
                type="email"
                label={emailInputLabel}
                keyPressEvent={onSubmit}
                errorMessage="Your email is incorrect"
                showErrorFromParent={error}
                mixPanelServiceTrackName={name}
                mixPanelTrackProp="LeadCapturePopupFocus"
              />
            </div>
            <div className={styles.PopupLeadCapture__consent}>
              <Checkbox
                id="lead-capture-consent"
                checked={isConsentChecked}
                onChange={handleCheckboxChange}
              >
                {consent}
              </Checkbox>
            </div>
            <div className={styles.PopupLeadCapture__signupButton}>
              <Button disabled={!isConsentChecked} theme="brandPurple" onClick={onSubmit}>
                {buttonText}
              </Button>
              {secondaryButton && (
                <Button theme="ghost" onClick={onDismissWithSecondaryButton}>
                  {secondaryButton}
                </Button>
              )}
            </div>
          </div>
        )}

        {step === PopupLeadCaptureSteps.Success && (
          <div className={styles.PopupLeadCapture__successStep}>
            <h1 className={styles.PopupLeadCapture__successTitle}>{successTitle}</h1>
            <div
              className={styles.PopupLeadCapture__successText}
              dangerouslySetInnerHTML={{ __html: successText }}
            />
          </div>
        )}

        {step === PopupLeadCaptureSteps.Signup && (
          <div className={styles.PopupLeadCapture__signupStep}>
            <h1 className={styles.PopupLeadCapture__signupTitle}>{successTitle}</h1>
            <div
              className={styles.PopupLeadCapture__signupText}
              dangerouslySetInnerHTML={{ __html: successText }}
            />
            <p className={styles.PopupLeadCapture__signupCode}>
              {discountLine} <b>{promoCode}</b>
            </p>
            <div
              className={styles.PopupLeadCapture__buttons}
              aria-label={`Sign up with discount code ${promoCode}`}
            >
              <SignupButton theme="brandPurple" code={promoCode}>
                {signupDiscountButton}
              </SignupButton>
              <Button theme="ghost" onClick={saveDiscountCode}>
                {saveDiscountButton}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
